import { Brand, Review } from '@finn/ua-vehicle';
import { BasePageData, HeroCosmicData, ModuleType } from '@finn/ui-cosmic';
import { CosmicModuleType, getCosmicObject } from '@finn/ui-cosmic/ssr';
import { formatDate } from '@finn/ui-utils/ssr';

import { getProductGridContent } from '~/modules/cosmic/modules/ProductGrid/getProductGridContent';
import type { Hero2CosmicMetadata } from '~/ui-modules/Hero2/metaData';

import filters from '../localization/available.filters.json';
import { getBreadcrumbFromSlug } from '../modules/cosmic/modules/Breadcrumbs/Breadcrumbs';
import { ProductGridCosmicData } from '../modules/cosmic/modules/ProductGrid/ProductGridMetadata';
import { getReviews } from '../services/reviews';
import { buildPageMeta } from '../ui-modules/PageBodyBuilder/custom-html-tags/pageBuilder';
import { getProductSliderContent } from '../ui-modules/ProductSlider/getProductSliderContent';
import { getTags } from '../ui-modules/TaggingSystem/helpers';

export interface LandingPageProps {
  pageData: BasePageData | null;
  reviews: Review[];
  brands: Brand[];
  slug: string;
  shouldConsiderAsLandingPage: boolean;
  jsonLD?: object | null;
}

interface JsonLDParams {
  date: string;
  url: string;
  img: string;
  title: string;
  description?: string;
  originalSlugs?: string[];
  aggregateRating?: object | null;
}

const getJsonLD = ({
  url,
  date,
  title,
  description,
  img,
  originalSlugs,
  aggregateRating,
}: JsonLDParams) => {
  const isCarBrandPage =
    originalSlugs?.[1] === 'marken-modelle' &&
    !url.endsWith('-automarken') &&
    originalSlugs.length === 3;

  if (isCarBrandPage && aggregateRating) {
    const brand = getBreadcrumbFromSlug(originalSlugs?.[2]);

    return {
      '@context': 'https://schema.org',
      '@type': 'Car',
      name: brand,
      manufacturer: {
        '@type': 'Organization',
        name: brand,
      },
      aggregateRating: {
        ...aggregateRating,
      },
    };
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    headline: title,
    description,
    image: img,
    author: {
      '@type': 'Organization',
      name: 'FINN',
    },
    publisher: {
      '@type': 'Organization',
      name: '',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.finn.com/assets/favicon/favicon-32x32.png',
      },
    },
    datePublished: date,
  };
};

// takes aggregate rating data from Cosmic and returns JSON-LD object
// if exact URL data is not found it tries to match wildcard url
const extractJsonLDFromCosmic = ({
  path,
  data,
}: {
  path: string;
  data: { [key: string]: object } | undefined;
}): object | null => {
  if (!data) {
    return null;
  }

  if (data?.[path]) {
    return data[path];
  }
  const wildcardURLs = Object.keys(data)
    .filter((key) => key.endsWith('/*'))
    .map((key) => key.slice(0, -1));
  // if any of wildcards is matching page path
  const matchingWildcard = wildcardURLs.find((key) => path.startsWith(key));
  if (matchingWildcard) {
    return data?.[`${matchingWildcard}*`];
  }

  return null;
};

// according to new URL structure by SEO team all articles with URL /auto/TAG should serve the guide Page with Tags Module

const isSlugOfTagBasedPage = (slug?: string | string[]): boolean => {
  if (typeof slug === 'string') {
    return slug.split('-').length === 2 && slug.split('-')[0] === 'auto';
  }

  return Boolean(slug?.length === 2 && slug?.[0] === 'auto');
};

interface PageInfo {
  slug: string;
  title: string;
  imageURL: string;
  imageAlt: string;
  publishedAt: string;
}

const extractPageInfo = (pageData: BasePageData): PageInfo => {
  const { modules, seo_header } = buildPageMeta(pageData);

  const heroModule: HeroCosmicData | undefined = modules?.find(
    (module: ModuleType) => module?.type === 'web-module-heroes'
  ) as HeroCosmicData | undefined;
  const splitHeroModule = modules?.find(
    (module: ModuleType) => module?.type === 'web-module-heroes-2'
  );

  const image =
    heroModule?.metadata?.hero_image ||
    heroModule?.metadata?.image ||
    (splitHeroModule?.metadata as Hero2CosmicMetadata)?.hero_image ||
    (splitHeroModule?.metadata as Hero2CosmicMetadata)?.image;

  const imageURL =
    (image && 'imgix_url' in image && image.imgix_url) ||
    (image && 'metadata' in image && image.metadata?.small?.imgix_url) ||
    (image && 'src' in image && image.src) ||
    (image && 'metadata' in image && image.metadata?.small?.src) ||
    (seo_header &&
      'metadata' in seo_header &&
      seo_header.metadata?.og_image?.src);

  const imageAlt =
    (heroModule &&
      'metadata' in heroModule &&
      heroModule.metadata?.hero_image_alt_text) ||
    (seo_header &&
      'metadata' in seo_header &&
      seo_header.metadata?.og_image?.alt);

  const title =
    heroModule?.metadata?.title ||
    (splitHeroModule?.metadata as Hero2CosmicMetadata)?.title ||
    seo_header?.metadata?.title ||
    seo_header?.metadata?.og_title;

  return {
    slug: pageData.slug,
    title: title || '',
    imageURL: imageURL || '',
    imageAlt: imageAlt || '',
    publishedAt: (pageData as any).objects?.[0]?.published_at || '',
  };
};

// we find breadcrumbs module in the pageData and remove it from the modules array
// than we re-add it as first module in the array, we using remove module data
// as overrides
const addBreadcrumbsModuleIfRequired = (
  pageData: BasePageData,
  slugArray: string[]
): void => {
  const pageInfo = extractPageInfo(pageData);

  // Ensure modules array exists
  if (!pageData.metadata.modules) {
    pageData.metadata.modules = [];
  }

  const index = pageData.metadata.modules.findIndex(
    (module: ModuleType) =>
      module?.type === CosmicModuleType.WEB_MODULE_BREADCRUMBS
  );
  const breadcrumbsModule =
    index !== -1 ? pageData.metadata.modules[index] : undefined;

  if (breadcrumbsModule) {
    pageData.metadata.modules = pageData.metadata.modules.filter(
      (module: ModuleType) =>
        module?.type !== CosmicModuleType.WEB_MODULE_BREADCRUMBS
    );
  }

  pageData.metadata.modules.unshift({
    type: CosmicModuleType.WEB_MODULE_BREADCRUMBS,
    metadata: {
      tags: pageData?.metadata?.tags,
      slugArray:
        breadcrumbsModule && 'metadata' in breadcrumbsModule
          ? (breadcrumbsModule.metadata as any)?.slugArray || slugArray
          : slugArray,
      heading:
        breadcrumbsModule && 'metadata' in breadcrumbsModule
          ? (breadcrumbsModule.metadata as any)?.heading || 'p'
          : 'p',
      title:
        (pageData as any)?.metadata?.primary_keyword ||
        (breadcrumbsModule && 'metadata' in breadcrumbsModule
          ? (breadcrumbsModule.metadata as any)?.title
          : undefined) ||
        pageInfo.title,
    },
    slug: 'breadcrumbs',
  } as unknown as ModuleType);
};

const enableSmartProductGrid = (pageData: BasePageData): void => {
  if (!pageData.metadata.modules) {
    return;
  }

  const productGridModule = pageData.metadata.modules.find(
    (module: ModuleType) =>
      module?.type === CosmicModuleType.WEB_MODULE_PRODUCT_GRID
  ) as ProductGridCosmicData;

  if (
    productGridModule &&
    'metadata' in productGridModule &&
    productGridModule.metadata
  ) {
    productGridModule.metadata.smartProductGrid = true;
  }
};

// auto-abo is new page, it is a special landing done to overrank the home page
// for details, contact SEO team
export const breadcrumbsExceptions = ['auto-abo'];

export const getLandingPageGetServerSideProps = async (
  slug: string,
  locale: string,
  isExactSlug = false,
  {
    slugArray,
    originalSlugs,
  }: { slugArray?: string[]; originalSlugs?: string[] } = {}
): Promise<LandingPageProps> => {
  const standardSlug = isExactSlug ? slug : `page-${slug}`;
  const cosmicObjectSlug = isSlugOfTagBasedPage(slugArray)
    ? `page-auto`
    : standardSlug;

  const pageDataRaw = await getCosmicObject({
    slug: cosmicObjectSlug,
    locale,
  });

  const pageData = pageDataRaw?.metadata
    ? {
        ...pageDataRaw,
        metadata: {
          ...pageDataRaw?.metadata,
          ...buildPageMeta({ metadata: pageDataRaw?.metadata }),
        },
      }
    : pageDataRaw;

  if (pageData?.metadata && pageData?.metadata?.seo_header === undefined) {
    pageData.metadata.seo_header = null;
  }

  let jsonLD: object | null = null;
  if (
    slug?.startsWith('blog') ||
    slug?.startsWith('guide') ||
    slug?.startsWith('auto-')
  ) {
    const seoHeader = pageData?.metadata?.seo_header?.metadata;
    const hero = pageData?.metadata?.modules.find(
      (module: ModuleType) =>
        module?.type === CosmicModuleType.WEB_MODULE_HERO ||
        module?.type === CosmicModuleType.WEB_MODULE_HERO_2
    ) as HeroCosmicData;
    const aggregateRatingFromCosmic = await getCosmicObject({
      slug: 'meta-aggregate-rating',
      locale,
    });
    const aggregateRating = extractJsonLDFromCosmic({
      path: `/${originalSlugs?.join('/')}`,
      data: aggregateRatingFromCosmic?.metadata?.data,
    });

    if (seoHeader?.title) {
      jsonLD = getJsonLD({
        title: hero?.metadata?.title || seoHeader.title,
        description: seoHeader.description,
        img: hero?.metadata?.hero_image?.url || seoHeader.og_image?.url || '',
        date: formatDate(
          pageData?.modified_at || new Date().toISOString(),
          'YYYY-MM-DD'
        ),
        url: `https://www.finn.com/${locale}/${slugArray?.join('/')}`,
        aggregateRating,
        originalSlugs,
      });
    }
  }

  // we need to set page_type for blog pages
  // so that we can use it in the PageBodyBuilder to find difference
  // between blog and other pages, as we want different styles for blog pages
  if (
    pageData?.metadata &&
    slugArray &&
    slugArray.length > 1 &&
    cosmicObjectSlug !== 'page-auto' &&
    cosmicObjectSlug !== 'page-guide'
  ) {
    pageData.metadata.page_type = 'finn-blog';
  }

  if (pageData === null) {
    return {
      pageData: null,
      reviews: [],
      slug,
      shouldConsiderAsLandingPage: false,
      brands: [],
    };
  }
  enableSmartProductGrid(pageData);

  const productGridContent$ = getProductGridContent(
    pageData?.metadata?.modules || [],
    locale
  );

  const productSliderContent$ = getProductSliderContent({
    modules: pageData?.metadata?.modules || [],
    locale,
  });

  await Promise.all([productGridContent$, productSliderContent$]);

  const tags = await getTags();

  const youMayAlsoLikeModulesIndex = pageData?.metadata?.modules?.findIndex(
    (module: ModuleType) => module?.type === 'web-module-related'
  );

  if (
    youMayAlsoLikeModulesIndex !== undefined &&
    youMayAlsoLikeModulesIndex !== -1
  ) {
    const publishedRoutes = await getPublishedPages({ locale });

    if (publishedRoutes) {
      const publishedSlugs = publishedRoutes.map(
        (route: string) => `page-${route.split('/').join('-')}`
      );

      const currentModule =
        pageData.metadata.modules[youMayAlsoLikeModulesIndex];
      const recommendedArticles = currentModule.metadata?.articles?.map(
        (article: BasePageData) => {
          const pageInfo = extractPageInfo(article);
          const pageLinkIdx = publishedSlugs.findIndex(
            (item: string) => item === pageInfo.slug
          );

          return {
            ...pageInfo,
            link: pageLinkIdx !== -1 ? publishedRoutes[pageLinkIdx] : '#',
          };
        }
      );

      pageData.metadata.modules[youMayAlsoLikeModulesIndex] = {
        ...pageData.metadata.modules[youMayAlsoLikeModulesIndex],
        pageTags: pageData?.metadata.tags,
        recommendedArticles,
        allTagsData: tags,
      };
    }
  }

  // replacing guide hero with new Tagging module with back compatibility
  const heroModuleIdx =
    (cosmicObjectSlug === 'page-guide' || cosmicObjectSlug === 'page-auto') &&
    tags?.tags.length &&
    pageData?.metadata?.modules
      ? pageData.metadata.modules.findIndex(
          (item: ModuleType) =>
            item?.type === CosmicModuleType.WEB_MODULE_HERO_2
        )
      : -1;

  if (heroModuleIdx !== -1) {
    const heroModule = pageData.metadata.modules[heroModuleIdx];
    pageData.metadata.modules[heroModuleIdx] = {
      locale,
      type: CosmicModuleType.TAGS_HUB,
      metadata: {
        title:
          'metadata' in heroModule ? heroModule.metadata?.title : undefined,
        tags: tags.tags,
        pagesByTags: tags.pagesByTags,
      },
    } as unknown as ModuleType;
  }

  const slugsToUse = originalSlugs || slugArray;
  if (
    slugsToUse &&
    (slugsToUse.length > 2 || breadcrumbsExceptions.includes(slugsToUse[0]))
  ) {
    addBreadcrumbsModuleIfRequired(pageData, slugsToUse);
  }

  let { brands } = filters['de-DE'];
  if (locale === 'en-US') {
    brands = [];
  }

  // Todo: this one can also be cached
  const { data: reviews } = await getReviews({ limit: 10, minScore: 8.0 });

  return {
    jsonLD: jsonLD ?? undefined,
    pageData,
    reviews,
    brands,
    slug,
    shouldConsiderAsLandingPage: true,
  };
};

export const migrateToEnRoutesRedirects: { [key: string]: string } = {
  'subscription-1-month': 'auto-abo-1-monat',
  'flexible-leasing': 'flexibles-leasing',
  'car-flatrate': 'auto-flatrate',
  'electric-car': 'elektroauto',
  'shortterm-leasing': 'kurzzeit-leasing',
  longtermrental: 'langzeitmiete',
  'monthlyrent-car': 'monatsmiete-auto',
};

interface PublishedPage {
  route: string;
}

const getPublishedPages = async ({
  locale,
}: {
  locale: string;
}): Promise<string[] | undefined> => {
  const res = await getCosmicObject({
    slug: 'published-pages-repeater',
    locale,
  });
  const metadata = res?.metadata;

  const landingPagesList = metadata?.published_pages?.map(
    (page: PublishedPage) => page.route
  );

  return landingPagesList;
};

export const shouldConsiderAsLandingPage = async (
  pathParams: string[],
  locale: string
): Promise<boolean> => {
  const landingPagesList = await getPublishedPages({ locale });

  if (!landingPagesList && !isSlugOfTagBasedPage(pathParams.join('-'))) {
    return false;
  }

  const isNested = (pathParams?.length ?? 0) > 1;

  const slug = isNested ? pathParams.join('/') : pathParams[0] || '';

  return (
    landingPagesList?.includes(slug) ||
    landingPagesList?.includes(migrateToEnRoutesRedirects[slug]) ||
    isSlugOfTagBasedPage(pathParams.join('-'))
  );
};
